import { useContext, useEffect, useRef, useState } from 'react';
import axios from "axios";
import { navigate } from "raviger";
import { Field, Formik } from 'formik';
import {
  ButtonGroup,
  Card,
  Col,
  Form as BootstrapForm,
  OverlayTrigger,
  Row,
  Tooltip
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowAltCircleLeft,
  faInfoCircle,
} from '@fortawesome/free-solid-svg-icons';
import * as Yup from 'yup';
import { MapContainer, Marker, Tooltip as MapTooltip, ScaleControl, TileLayer } from "react-leaflet";
import { BoundsUpdater, LocationMarker, createPinMarkerIcon } from "../components/Map";
import { AddressLookup, TextInput, ToggleSwitch } from '../components/Form.js';
import ButtonSpinner from '../components/ButtonSpinner.js';
import { AuthContext } from "../accounts/AccountsReducer";
import { SystemErrorContext } from '../components/SystemError';
import EvacZonesLayer from '../components/EvacZonesLayer';
import FirePerimetersLayer from '../components/FirePerimetersLayer';
import CaltopoFeaturesLayer from '../components/CaltopoFeaturesLayer';

const IncidentForm = ({ id, organization }) => {

  const { state } = useContext(AuthContext);
  const { setShowSystemError } = useContext(SystemErrorContext);

  const [data, setData] = useState({
    name: '',
    description: '',
    slug: '',
    latitude: '',
    longitude: '',
    default_followup_days: '1',
    training: false,
    publicly_discoverable: false,
    caltopo_map_id: '',
    watchduty_map_id: '',
    organization: state.organization.id,
    reunification_details: '',
    notify_all: true,
  });

  const [newSlug, setNewSlug] = useState("");
  const [pinMarkerIcon, setPinMarkerIcon] = useState(createPinMarkerIcon(13));
  const [bounds, setBounds] = useState([[24.396308, -125.0], [49.384358, -66.93457]]);
  const [names, setNames] = useState([]);

  const markerRef = useRef(null);
  const mapRef = useRef(null);

  const get_slug = (incident_name) => {
    const new_slug = incident_name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')  // Replace any group of non-alphanumerics with a single dash
    .replace(/^-+|-+$/g, '');
    setNewSlug(new_slug);
    return new_slug;
  }

  const updatePosition = (setFieldValue) => {
    const marker = markerRef.current;
    const map = mapRef.current
    const latLon = marker.getLatLng();
    setFieldValue("latlon", latLon.lat.toFixed(4) + ', ' + latLon.lng.toFixed(4))
    setFieldValue("latitude", +(Math.round(latLon.lat + "e+4") + "e-4"));
    setFieldValue("longitude", +(Math.round(latLon.lng + "e+4") + "e-4"));
    map.setView(latLon);
  }

  const addMarker = (e, setFieldValue) => {
    if (!id) {
      setFieldValue("latlon", e.latlng.lat.toFixed(4) + ', ' + e.latlng.lng.toFixed(4))
      setFieldValue("latitude", +(Math.round(e.latlng.lat + "e+4") + "e-4"));
      setFieldValue("longitude", +(Math.round(e.latlng.lng + "e+4") + "e-4"));
    }
  }

  useEffect(() => {
    let unmounted = false;
    let source = axios.CancelToken.source();

    const fetchIncidents = async () => {
      // Fetch Incident data.
      await axios.get('/incident/api/incident/?organization_slug=' + organization, {
        cancelToken: source.token,
      })
      .then(response => {
        if (!unmounted) {
          const incident_bounds = [];
          const incident_names = [];
          const dupe_bounds = new Set();
          for (const incident of response.data) {
            incident_names.push(incident.slug);
            if (incident.latitude && incident.longitude && !incident.end_time) {
              let key = `${incident.latitude},${incident.longitude}`;
              if (!dupe_bounds.has(key)) {
                dupe_bounds.add(key);
                incident_bounds.push([parseFloat(incident.latitude), parseFloat(incident.longitude)]);
              }
            }
          }
          // Add in some extra bounds to prevent map zoom level from being too small with few or nearby incident locations.
          // if (incident_bounds.length >= 1) {
          //   incident_bounds.push([parseFloat(incident_bounds[0][0])+.04,parseFloat(incident_bounds[0][1])-.04])
          //   incident_bounds.push([parseFloat(incident_bounds[0][0])-.04,parseFloat(incident_bounds[0][1])+.04])
          // }
          if (!id) {
            setBounds(incident_bounds.length ? incident_bounds : [[24.396308, -125.0], [49.384358, -66.93457]]);
          }
          setNames(incident_names);
        }
      })
      .catch(error => {
        if (!unmounted) {
          setShowSystemError(true);
        }
      });
    };
    fetchIncidents();

    if (id) {
      const fetchIncident = async () => {
        // Fetch Visit Note data.
        await axios.get('/incident/api/incident/' + id + '/', {
          cancelToken: source.token,
        })
        .then(response => {
          if (!unmounted) {
            response.data['latlon'] = response.data['latitude'] + ', ' + response.data['longitude']
            setData(response.data);
            setBounds([[parseFloat(response.data['latitude']), parseFloat(response.data['longitude'])]]);
          }
        })
        .catch(error => {
          if (!unmounted) {
            setShowSystemError(true);
          }
        });
      };
      fetchIncident();
    }

    // Cleanup.
    return () => {
      unmounted = true;
      source.cancel();
    };
  }, [id]);

  return (
    <Formik
      initialValues={data}
      enableReinitialize={true}
      validationSchema={Yup.object({
        name: Yup.string()
          .max(20, 'Must be 20 characters or less')
          .required('Required')
          .test('required-check', 'Name already in use.',
            function(value) {
              // Check against slug for dupes.
              if (value && data.name !== value && names.includes(get_slug(value))) {
                return false;
              }
              return true;
            }),
        description: Yup.string().max(500, 'Must be 500 characters or less.').nullable(),
        reunification_details: Yup.string().max(1000, 'Must be 1000 characters or less.').nullable(),
        latlon: Yup.string().required('Required'),
        latitude: Yup.number(),
        longitude: Yup.number(),
        default_followup_days: Yup.number().integer().required('Required'),
        training: Yup.boolean(),
        caltopo_map_id: Yup.string()
          .nullable()
          .max(10, 'Must be 10 characters or less')
          .test('map-id-check', 'CalTopo Map ID is Invalid.', function(value) {
            if (!value) return true;
            return axios
              .get(`/incident/api/incident/caltopo_check/`, { params: { map_id: value } })
              .then((response) => {
                return response.data.valid;
              })
              .catch((err) => {
                return false;
              });
          }),
        watchduty_map_id: Yup.string().nullable().max(10, 'Must be 10 characters or less'),
        notify_all: Yup.boolean()
      })}
      onSubmit={(values, { setSubmitting }) => {
        values['slug'] = get_slug(values.name);
        if (id) {
          axios.put('/incident/api/incident/' + id + '/?organization=' + state.organization.id, values)
          .then(function () {
            navigate('/' + organization + '/manage/incidents');
          })
          .catch(error => {
            setShowSystemError(true);
          });
        }
        else {
          axios.post('/incident/api/incident/?organization=' + state.organization.id, values)
          .then(function () {
            navigate('/' + organization + '/manage/incidents');
          })
          .catch(error => {
            setShowSystemError(true);
          });
        }
      }}
    >
      {form => (
        <Card border="secondary" className="mt-4 ms-auto me-auto" style={{width:"50%", maxWidth:"50%"}}>
          <Card.Header as="h5" className="ps-3 d-flex flex-row justify-content-between">
            <div>
              <span style={{ cursor: 'pointer' }} onClick={() => navigate("/" + organization + '/manage/incidents')} className="me-3">
                <FontAwesomeIcon icon={faArrowAltCircleLeft} size="lg" inverse /></span>{id ? 'Update' : 'New'} Incident
            </div>
          </Card.Header>
          <Card.Body>
            <BootstrapForm>
              <Row>
                <TextInput
                  type="text"
                  label={`Name* (/${organization}/${newSlug ? newSlug : data.slug}/)`}
                  name="name"
                  id="name"
                  xs="12"
                />
              </Row>
              <Row className="mt-3">
                <TextInput
                  type="text"
                  as="textarea"
                  rows={3}
                  label="Description"
                  name="description"
                  id="description"
                  xs="12"
                />
              </Row>
              <Row className="mt-3">
                <TextInput
                  type="text"
                  as="textarea"
                  rows={3}
                  label="Reunification Details"
                  name="reunification_details"
                  id="reunification_details"
                  xs="12"
                />
              </Row>
              <Row className="mt-3">
                <TextInput
                  type="text"
                  rows={3}
                  label="Default Followup Days"
                  name="default_followup_days"
                  id="default_followup_days"
                  xs="12"
                />
              </Row>
              <Row className="mt-3 mb-3">
              {state.organization.caltopo_enabled ?
                <TextInput
                  type="text"
                  rows={3}
                  label="CalTopo Map ID"
                  name="caltopo_map_id"
                  id="caltopo_map_id"
                  xs="12"
                /> : ""}
              {/* state.organization.watchduty_enabled ?
                <TextInput
                  type="text"
                  rows={3}
                  label="WatchDuty Map ID"
                  name="watchduty_map_id"
                  id="watchduty_map_id"
                  xs="12"
                /> : ""*/}
              </Row>
              <label style={{marginBottom:"8px"}}>Location Finder</label>
              <AddressLookup state={state} formikProps={form} style={{width: '100%'}} className={"form-control"} initialBounds={bounds} setBounds={setBounds} incident={null} showclear={false} />
              <Row className="mt-3">
                <TextInput
                  type="text"
                  label="Lat/Lon*"
                  name="latlon"
                  id="latlon"
                  xs="12"
                  disabled
                  onChange={(e) => {
                    const lookup = e.target.value.replace(' ', '').split(',');
                    if (lookup[0] <= 90 && lookup[0] >= -90 && lookup[1] <= 180 && lookup[1] >= -180) {
                      form.setFieldValue("latitude", Number(lookup[0]));
                      form.setFieldValue("longitude", Number(lookup[1]));
                    }
                    form.setFieldValue("latlon", e.target.value);
                  }}
                  value={form.values.latlon || ''}
                />
              </Row>
              <Row className="mt-3">
                <Col xs="12">
                  <BootstrapForm.Label>Refine Incident Lat/Lon Point</BootstrapForm.Label>
                  <MapContainer zoom={11} ref={mapRef} bounds={bounds.length > 1 ? bounds : null} center={bounds.length === 1 ? [bounds[0][0], bounds[0][1]] : null} boundsOptions={{padding:[10,10]}} onClick={(e) => {addMarker(e, form.setFieldValue)}} className="incident-leaflet-container border rounded" setPinMarkerIcon={setPinMarkerIcon}>
                    <ScaleControl position="bottomleft" metric={false} />
                    <BoundsUpdater bounds={bounds} />
                    <TileLayer
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
                    />
                    <LocationMarker id={id} setFieldValue={form.setFieldValue} values={form.values} draggable={true} updatePosition={updatePosition} ref={markerRef} />
                    <EvacZonesLayer />
                    <FirePerimetersLayer />
                    {state?.incident?.id && (
                      <CaltopoFeaturesLayer id={state.incident.id} />
                    )}
                    {/* {form.values.latitude && form.values.longitude ?
                    <Marker
                      draggable={true}
                      eventHandlers={{
                        dragend() {
                          updatePosition(form.setFieldValue)
                        },
                      }}
                      // onDragEnd={() => {
                      //   updatePosition(form.setFieldValue)
                      // }}
                      position={[form.values.latitude, form.values.longitude]}
                      icon={pinMarkerIcon}
                      ref={markerRef}
                    >
                      <MapTooltip direction="top">
                        <div>
                          Lat: {form.values.latitude}, Lon: {form.values.longitude}
                        </div>
                      </MapTooltip>
                    </Marker>
                    : ""} */}
                  </MapContainer>
                </Col>
              </Row>
              <Row className="mt-3">
                <ToggleSwitch name="training" id="training" label="Training" onChange={(e) => {
                  form.setFieldValue("training", !form.values.training);
                  if (e.target.checked === true) {
                    form.setFieldValue("publicly_discoverable", false);
                    form.setFieldValue("notify_all", false)
                  }
                }} />
                {!id && (
                  <ToggleSwitch xs="3" name="notify_all" id="notify_all" label="Notify All Admins"/>
                )}
                <ToggleSwitch xs="3" name="publicly_discoverable" id="publicly_discoverable" label={<span>Publicly Discoverable<OverlayTrigger
                  key={"user-perms"}
                  placement="top"
                  overlay={
                    <Tooltip id={`tooltip-user-perms`}>
                      Publicly discoverable incidents will always show up in the list of incidents on the public animal search page and do not required being specified in the URL. Training incidents may not be publicly discoverable.
                    </Tooltip>
                  }
                >
                  <FontAwesomeIcon icon={faInfoCircle} className="fa-move-up ms-1" />
                </OverlayTrigger></span>} disabled={form.values['training'] === true} />
              </Row>
            </BootstrapForm>
          </Card.Body>
          <ButtonGroup size="lg">
            <ButtonSpinner isSubmitting={form.isSubmitting} isSubmittingText="Saving..." className="btn btn-primary border" onClick={() => { form.submitForm() }}>Save</ButtonSpinner>
          </ButtonGroup>
        </Card>
      )}
    </Formik>
  );
};

export default IncidentForm;
